import { supabase } from "./supabaseClient";

const NOTIFICATION_ENABLED_KEY = "notifications_enabled";
const LAST_NOTIFICATION_KEY = "lastDailyNotification";
const NOTIFICATION_CHECK_INTERVAL = 60 * 1000; // Check every minute

let checkInterval: number | null = null;

export function isNotificationsEnabled(): boolean {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem(NOTIFICATION_ENABLED_KEY);
  // Default to enabled if not set
  return stored === null || stored === "true";
}

export function setNotificationsEnabled(enabled: boolean) {
  if (typeof window === "undefined") return;
  localStorage.setItem(NOTIFICATION_ENABLED_KEY, String(enabled));

  // If enabling, start checking for notifications
  if (enabled) {
    startNotificationChecker();
    checkAndSendNotification(); // Check immediately
  } else {
    // Disable notifications
    stopNotificationChecker();
    localStorage.removeItem(LAST_NOTIFICATION_KEY);
  }
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!("Notification" in window)) {
    console.log("This browser does not support notifications");
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  return false;
}

// Check if it's time to send a notification (4pm)
function shouldSendNotification(): boolean {
  const now = new Date();
  const hour = now.getHours();
  const minute = now.getMinutes();

  // Check if it's 4pm (16:00) - check between 16:00 and 16:04 to catch the notification window
  // This gives us a 5-minute window to catch the notification
  return hour === 16 && minute >= 0 && minute < 5;
}

// Check if notification was already sent today
function wasNotificationSentToday(): boolean {
  const lastNotification = localStorage.getItem(LAST_NOTIFICATION_KEY);
  if (!lastNotification) return false;

  const today = new Date().toDateString();
  return lastNotification === today;
}

// Main function to check and send notification
async function checkAndSendNotification() {
  // Check if notifications are enabled by user
  if (!isNotificationsEnabled()) {
    return;
  }

  const hasPermission = await requestNotificationPermission();
  if (!hasPermission) {
    return;
  }

  // Check if notification was already sent today
  if (wasNotificationSentToday()) {
    return;
  }

  // Check if it's time to send (4pm)
  if (shouldSendNotification()) {
    await sendDailyTaskSummary();
  }
}

// Start periodic checking for notifications
function startNotificationChecker() {
  // Clear any existing interval
  stopNotificationChecker();

  // Check immediately
  checkAndSendNotification();

  // Then check every minute
  checkInterval = window.setInterval(() => {
    checkAndSendNotification();
  }, NOTIFICATION_CHECK_INTERVAL);
}

// Stop periodic checking
function stopNotificationChecker() {
  if (checkInterval !== null) {
    clearInterval(checkInterval);
    checkInterval = null;
  }
}

async function sendDailyTaskSummary() {
  const hasPermission = await requestNotificationPermission();
  if (!hasPermission) {
    return;
  }

  // Double-check we haven't sent today (race condition protection)
  if (wasNotificationSentToday()) {
    return;
  }

  try {
    // Get today's date in YYYY-MM-DD format
    const today = new Date();
    today.setUTCHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split("T")[0];

    // Get user's projects
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) {
      return;
    }

    const { data: projectsData } = await supabase.rpc("get_user_projects");
    if (!projectsData) {
      return;
    }

    const projectIds = projectsData.map((p) => p.project_id);

    if (projectIds.length === 0) {
      // No projects, but still send a notification
      new Notification("Todox - Daily Summary", {
        body: "You're all caught up! No tasks due today.",
        icon: "/pwa-192x192.png",
        badge: "/pwa-192x192.png",
        tag: "daily-summary",
      });
      // Mark as sent
      localStorage.setItem(LAST_NOTIFICATION_KEY, today.toDateString());
      return;
    }

    // Get tasks due today
    const { data: tasks } = await supabase
      .from("tasks")
      .select("*")
      .in("project_id", projectIds)
      .eq("due_date", todayStr);

    if (!tasks || tasks.length === 0) {
      // No tasks today
      new Notification("Todox - Daily Summary", {
        body: "You're all caught up! No tasks due today.",
        icon: "/pwa-192x192.png",
        badge: "/pwa-192x192.png",
        tag: "daily-summary",
      });
    } else {
      // Show summary of tasks
      const pendingTasks = tasks.filter((t) => t.status !== "done");
      const completedTasks = tasks.filter((t) => t.status === "done");
      const pendingCount = pendingTasks.length;
      const completedCount = completedTasks.length;

      let body = "";
      if (pendingCount > 0) {
        body = `You have ${pendingCount} task${pendingCount !== 1 ? "s" : ""} due today`;
        if (completedCount > 0) {
          body += ` and ${completedCount} completed`;
        }
      } else if (completedCount > 0) {
        body = `All ${completedCount} task${completedCount !== 1 ? "s" : ""} due today ${completedCount === 1 ? "is" : "are"} completed! Great work!`;
      } else {
        body = "You're all caught up! No tasks due today.";
      }

      new Notification("Todox - Daily Summary", {
        body,
        icon: "/pwa-192x192.png",
        badge: "/pwa-192x192.png",
        tag: "daily-summary",
        data: {
          url: "/today",
        },
      });
    }

    // Mark notification as sent today
    localStorage.setItem(LAST_NOTIFICATION_KEY, today.toDateString());
    console.log("Daily notification sent at", new Date().toLocaleTimeString());
  } catch (error) {
    console.error("Error sending daily notification:", error);
  }
}

// Initialize notification scheduling when app loads
export function initializeNotifications() {
  if (typeof window !== "undefined" && isNotificationsEnabled()) {
    // Check if we missed a notification (e.g., if app was closed at 4pm)
    // This handles the case where the app was closed and reopened after 4pm
    const now = new Date();
    const hour = now.getHours();

    // If it's past 4pm today and we haven't sent a notification, send it now
    if (hour >= 16 && !wasNotificationSentToday()) {
      checkAndSendNotification();
    }

    // Start periodic checking
    startNotificationChecker();
  }
}

// Cleanup on page unload
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    stopNotificationChecker();
  });

  // Also handle visibility change (when tab becomes hidden/visible)
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && isNotificationsEnabled()) {
      // Check if we missed a notification while the tab was hidden
      const now = new Date();
      const hour = now.getHours();

      if (hour >= 16 && !wasNotificationSentToday()) {
        checkAndSendNotification();
      }

      // Restart checker if it was stopped
      if (checkInterval === null) {
        startNotificationChecker();
      }
    }
  });
}

// Get current notification permission status
export function getNotificationPermission(): "default" | "granted" | "denied" {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "denied";
  }
  return Notification.permission;
}